import React, { useState, useEffect,useRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { userAPI, sessionAPI } from "../../services/api";

// Skeleton Loader Component
const TableSkeleton = () => (
  <div className="w-full">
    {/* Desktop Table Header Skeleton */}
    <div className="hidden md:grid grid-cols-5 gap-4 items-center text-xs bg-gray-100 font-medium text-gray-600 uppercase tracking-wider mb-4 px-4 py-4 rounded-lg">
      <div>Patient Name</div>
      <div>Doctors</div>
      <div>Type</div>
      <div>Date & Time</div>
      <div>Status</div>
    </div>
    
    {/* Desktop Table Rows Skeleton */}
    <div className="hidden md:block">
    {[...Array(8)].map((_, idx) => (
      <div
        key={idx}
          className="grid grid-cols-5 gap-4 items-center border rounded-xl px-4 py-4 my-2 bg-white shadow-sm border-gray-200"
      >
        {/* Patient Name */}
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-gray-200 rounded-full animate-pulse"></div>
          <div className="h-4 bg-gray-200 rounded w-24 animate-pulse"></div>
        </div>
        {/* Doctor Name */}
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-gray-200 rounded-full animate-pulse"></div>
          <div className="h-4 bg-gray-200 rounded w-28 animate-pulse"></div>
        </div>
        {/* Session Type */}
        <div className="h-6 bg-gray-200 rounded w-20 animate-pulse"></div>
        {/* Date & Time */}
        <div className="space-y-1">
          <div className="h-4 bg-gray-200 rounded w-32 animate-pulse"></div>
          <div className="h-3 bg-gray-200 rounded w-20 animate-pulse"></div>
        </div>
        {/* Status */}
        <div className="h-6 bg-gray-200 rounded w-16 animate-pulse"></div>
      </div>
    ))}
    </div>

    {/* Mobile Card Skeleton */}
    <div className="md:hidden space-y-3">
      {[...Array(6)].map((_, idx) => (
        <div
          key={idx}
          className="bg-white border rounded-xl p-4 shadow-sm border-gray-200"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-gray-200 rounded-full animate-pulse"></div>
              <div>
                <div className="h-4 bg-gray-200 rounded w-24 animate-pulse mb-1"></div>
                <div className="h-3 bg-gray-200 rounded w-16 animate-pulse"></div>
              </div>
            </div>
            <div className="h-6 bg-gray-200 rounded w-16 animate-pulse"></div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <div className="w-6 h-6 bg-gray-200 rounded-full animate-pulse"></div>
              <div className="h-4 bg-gray-200 rounded w-28 animate-pulse"></div>
            </div>
            <div className="flex items-center justify-between">
              <div className="h-6 bg-gray-200 rounded w-20 animate-pulse"></div>
              <div className="text-right">
                <div className="h-4 bg-gray-200 rounded w-24 animate-pulse mb-1"></div>
                <div className="h-3 bg-gray-200 rounded w-16 animate-pulse"></div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Sidebar Skeleton Components
const OverviewSkeleton = () => (
  <div className="bg-white p-4 sm:p-6 lg:p-8 rounded-xl shadow-md w-full">
    <div className="flex justify-between items-center mb-4 sm:mb-6">
      <div className="h-6 bg-gray-200 rounded w-20 animate-pulse"></div>
    </div>
    <div className="grid grid-cols-2 sm:grid-cols-2 gap-3 sm:gap-4">
      {[...Array(4)].map((_, idx) => (
        <div key={idx} className="bg-gray-50 rounded-lg p-4 sm:p-6 shadow-md">
          <div className="h-6 bg-gray-200 rounded w-8 animate-pulse mb-2"></div>
          <div className="h-3 bg-gray-200 rounded w-16 animate-pulse"></div>
        </div>
      ))}
    </div>
  </div>
);

const TodayScheduleSkeleton = () => (
  <div className="bg-white p-4 sm:p-5 rounded-xl shadow-md w-full">
    <div className="flex justify-between mb-3 items-center">
      <div className="h-6 bg-gray-200 rounded w-24 animate-pulse"></div>
      <div className="h-4 bg-gray-200 rounded w-12 animate-pulse"></div>
    </div>
    <div className="space-y-4">
      {[...Array(3)].map((_, idx) => (
        <div key={idx} className="flex items-center gap-3 text-sm">
          <div className="w-9 h-9 bg-gray-200 rounded-full animate-pulse flex-shrink-0"></div>
          <div className="flex-1">
            <div className="h-4 bg-gray-200 rounded w-24 animate-pulse mb-1"></div>
            <div className="h-3 bg-gray-200 rounded w-32 animate-pulse"></div>
          </div>
          <div className="h-3 bg-gray-200 rounded w-12 animate-pulse flex-shrink-0"></div>
        </div>
      ))}
    </div>
  </div>
);

const Session = () => {
  const [filters, setFilters] = useState({
    patientName: "",
    status: "",
    sessionType: "",
    nurseId: "",
    startDate: null,
    endDate: null,
  });

  const [doctors, setDoctors] = useState([]);
  const [loadingDoctors, setLoadingDoctors] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [pagination, setPagination] = useState({
      currentPage: 1,
      totalPages: 1,
      totalSessions: 0,
      hasNextPage: false,
    hasPrevPage: false,
    limit: 9,
    });
  
  // New state for statistics and today's schedule
  const [sessionStats, setSessionStats] = useState({
    ongoing: 0,
    pending: 0,
    missed: 0,
    completed: 0
  });
  const [todaySchedule, setTodaySchedule] = useState([]);
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingTodaySchedule, setLoadingTodaySchedule] = useState(true);
  
  // State for dropdown menu
  const [openDropdown, setOpenDropdown] = useState(null);
  const [showAllTodaySchedule, setShowAllTodaySchedule] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdown && !event.target.closest('.dropdown-container')) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdown]);

  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        setLoadingDoctors(true);
        const response = await userAPI.getNursesList(1, 100);
        if (response && response.nurses) {
          setDoctors(response.nurses);
        }
      } catch (error) {
        console.error("Error fetching nurses:", error);
      } finally {
        setLoadingDoctors(false);
      }
    };
    fetchDoctors();
  }, []);

  useEffect(() => {
    const fetchSessionStats = async () => {
      try {
        setLoadingStats(true);
        const response = await sessionAPI.getSessionStats();
        if (response.success) {
          const mappedStats = {
            ongoing: response.data.in_progress || 0,
            pending: response.data.scheduled || 0,
            missed: response.data.missed || 0,
            completed: response.data.completed || 0
          };
          setSessionStats(mappedStats);
        }
      } catch (error) {
        console.error("Error fetching session stats:", error);
      } finally {
        setLoadingStats(false);
      }
    };
    fetchSessionStats();
  }, []);


  useEffect(() => {
  const selectedDoctor = doctors.find(doc => doc._id === filters.nurseId);
  if (selectedDoctor) {
    setFilters(prev => ({
      ...prev,
      nurseId: selectedDoctor._id
    }));
  }
}, [filters.nurseId, doctors]);
  // Fetch today's schedule
  useEffect(() => {
    const fetchTodaySchedule = async () => {
      try {
        setLoadingTodaySchedule(true);
        const response = await sessionAPI.getTodaySchedule();
        
        if (response.success && response.data) {
          if (Array.isArray(response.data)) {
            const transformedSessions = response.data.map(session => ({
              patient: { 
                name: session.patientName || "Unknown Patient",
                profileImage: session.patientImage || ""
              },
              doctor: { 
                name: session.doctorName || "Unknown Provider",
                profileImage: ""
              },
              scheduledAt: session.startTime
            }));
            setTodaySchedule(transformedSessions);
          } else {
            setTodaySchedule([]);
          }
        } else {
          setTodaySchedule([]);
        }
      } catch (error) {
        console.error("Error fetching today's schedule:", error);
        setTodaySchedule([]);
      } finally {
        setLoadingTodaySchedule(false);
      }
    };
    fetchTodaySchedule();
  }, []);

const latestPageRef = useRef(pagination.currentPage);

useEffect(() => {
  const pageSnapshot = pagination.currentPage;
  latestPageRef.current = pageSnapshot;

  const timeoutId = setTimeout(() => {
    applyFilters(pageSnapshot); // ✅ correct
  }, 300);

  return () => clearTimeout(timeoutId);
}, [filters, pagination.currentPage]);

const applyFilters = async (page) => {
  try {
    setLoadingSessions(true);
    
    const apiFilters = {
      page: page,
      limit: pagination.limit
    };

    // Add filters with proper validation
    if (filters.patientName && filters.patientName.trim()) {
      apiFilters.patientName = filters.patientName.trim();
    }
    
    if (filters.nurseId) {
      apiFilters.nurseId = filters.nurseId;
    }
    
    if (filters.status) {
      apiFilters.status = filters.status;
    }
    
    if (filters.sessionType) {
      apiFilters.sessionType = filters.sessionType;
    }
    
    if (filters.startDate) {
      apiFilters.dateFrom = new Date(filters.startDate).toISOString();
    }
    
    if (filters.endDate) {
      const endDate = new Date(filters.endDate);
      endDate.setHours(23, 59, 59, 999);
      apiFilters.dateTo = endDate.toISOString();
    }

    console.log('Applying filters:', apiFilters);

    const response = await sessionAPI.getAdminSessions(page, pagination.limit, apiFilters);
    console.log('API response:', response);
    
    if (response.success) {
      setSessions(response.data.sessions);
      setPagination(prev => ({
        ...prev,
        currentPage: page,
        totalPages: response.data.pagination.totalPages || 1,
        totalSessions: response.data.pagination.totalItems || 0,
        hasNextPage: response.data.pagination.hasNextPage || false,
        hasPrevPage: response.data.pagination.hasPrevPage || false,
      }));

      if (response.data.stats) {
        setSessionStats({
          ongoing: response.data.stats.inProgress || 0,
          pending: response.data.stats.scheduled || 0,
          missed: response.data.stats.missed || 0,
          completed: response.data.stats.completed || 0
        });
      }
    }
  } catch (error) {
    console.error('Error applying filters:', error);
    setSessions([]);
    setPagination(prev => ({
      ...prev,
      currentPage: 1,
      totalPages: 1,
      totalSessions: 0,
      hasNextPage: false,
      hasPrevPage: false,
    }));
  } finally {
    setLoadingSessions(false);
  }
};

  // Handle page change
  const handlePageChange = (page) => {
    // Ensure page is within valid range
    if (page < 1 || page > pagination.totalPages) {
      return;
    }
    
    setPagination(prev => ({
      ...prev,
      currentPage: page,
    }));
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
    }));
    // Reset pagination to page 1 when filters change
    setPagination(prev => ({
      ...prev,
      currentPage: 1,
    }));
  };

  // Handle session status update
  const handleStatusUpdate = async (sessionId, newStatus) => {
    try {
      const response = await sessionAPI.updateSessionStatus(sessionId, newStatus);
      if (response.success) {
        setSessions(prevSessions => 
          prevSessions.map(session => 
            session._id === sessionId 
              ? { ...session, status: newStatus }
              : session
          )
        );
        
        // Refresh statistics after status update
        const statsResponse = await sessionAPI.getSessionStats();
        if (statsResponse.success) {
          setSessionStats({
            ongoing: statsResponse.data.in_progress || 0,
            pending: statsResponse.data.scheduled || 0,
            missed: statsResponse.data.missed || 0,
            completed: statsResponse.data.completed || 0
          });
        }
      }
    } catch (error) {
      console.error("Error updating session status:", error);
    }
  };

  // Handle join session
  const handleJoinSession = async (sessionId) => {
    try {
      const response = await sessionAPI.getSessionById(sessionId);
      if (response.success) {
        console.log("Joining session:", response.data);
      }
    } catch (error) {
      console.error("Error joining session:", error);
    }
  };

  // Format time for today's schedule
  const formatTime = (timeString) => {
    if (typeof timeString === 'string' && (timeString.includes('AM') || timeString.includes('PM'))) {
      return timeString;
    }
    
    if (typeof timeString === 'string' && timeString.includes(':') && !timeString.includes('AM') && !timeString.includes('PM')) {
      const [hours, minutes] = timeString.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${minutes} ${ampm}`;
    }
    
    if (timeString instanceof Date || (typeof timeString === 'string' && timeString.includes('T'))) {
      return new Date(timeString).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    }
    
    return timeString;
  };

  // Format session type for display
  const formatSessionType = (type) => {
    const typeMap = {
      'monthly_checkin': 'Monthly Check-in',
      'weekly_checkin': 'Weekly Check-in',
      // 'follow_up': 'Follow-Up',
      // 'medication_review': 'Medication Review',
      // 'assessment': 'Assessment',
      // 'consultation': 'Consultation',
      // 'emergency': 'Emergency',
      // 'initial_consultation': 'Initial Consultation',
      // 'initial': 'Initial Visit'
    };
    return typeMap[type] || type;
  };

  // Helper function to get correct image URL
  const getProfileImageUrl = (imagePath) => {
    if (!imagePath) return "https://cdn-icons-png.flaticon.com/512/149/149071.png";
    
    if (imagePath.startsWith('https')) {
      return imagePath;
    }
    
    const baseUrl = 'https://backend.adhdrapidcare.com';
    
    if (imagePath.startsWith('/')) {
      return `${baseUrl}${imagePath}`;
    }
    
    return `${baseUrl}/${imagePath}`;
  };

  return (
    <div className="min-h-screen ">
      <div className="mb-4 sm:mb-6 lg:mb-8">
        <h1 className="text-lg sm:text-xl lg:text-2xl font-semibold text-gray-800">
          Sessions
        </h1>
        <p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2">
          Track your session history, progress, and upcoming appointments in one
          place.
        </p>
      </div>

      {/* Filters - Responsive Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 sm:gap-4 mb-4 sm:mb-6">
        {/* Search Input */}
        <div className="relative sm:col-span-2 lg:col-span-1">
         <input
  type="text"
  value={filters.patientName}
  onChange={(e) => handleFilterChange("patientName", e.target.value)}
  placeholder="Search patient"
  className="pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm w-full placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
/>
          <svg
            className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>

        {/* Date Range */}
        <div className="sm:col-span-2 lg:col-span-1">
            <div className="relative w-full">
          <DatePicker
            selectsRange
            startDate={filters.startDate}
            endDate={filters.endDate}
            onChange={(update) => {
              handleFilterChange("startDate", update[0]);
              handleFilterChange("endDate", update[1]);
            }}
            placeholderText="Select date range"
            className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            isClearable
            dateFormat="MMM dd, yyyy"
          />
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
            <svg
              className="w-4 h-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          </div>
        </div>
        </div>

     
        <div className="relative">
          <select
  value={filters.status}
  onChange={(e) => handleFilterChange("status", e.target.value)}
  className="appearance-none w-full px-3 py-2 border bg-white border-gray-300 rounded-md text-sm text-black focus:outline-none focus:ring-2 focus:ring-blue-500"
>
            <option value="">All Status</option>
  <option value="scheduled">Scheduled</option>
  <option value="in_progress">In Progress</option>
  <option value="completed">Completed</option>
  <option value="missed">Missed</option>
</select>
          <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
            <svg
              className="w-4 h-4 text-black"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>

        {/* Session Type Filter */}
        <div className="relative">
          <select
            value={filters.sessionType || ''}
            onChange={(e) => handleFilterChange("sessionType", e.target.value)}
            className="appearance-none w-full px-3 py-2 border bg-white border-gray-300 rounded-md text-sm text-black focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Session Type</option>
            <option value="monthly_checkin">Monthly Check-in</option>
            <option value="weekly_checkin">Weekly Check-in</option>
            {/* <option value="follow_up">Follow-up</option>
            <option value="emergency">Emergency</option>
            <option value="initial">Initial Visit</option>
            <option value="missed">Missed</option> */}
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
            <svg
              className="w-4 h-4 text-black"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>

        {/* Provider */}
        <div className="relative sm:col-span-2 lg:col-span-1">
          <select
            className="appearance-none w-full px-3 py-2 border bg-white border-gray-300 rounded-md text-sm text-black focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={filters.nurseId}
            onChange={(e) => handleFilterChange("nurseId", e.target.value)}
            disabled={loadingDoctors}
          >
            <option value="">
              {loadingDoctors ? "Loading providers..." : "Select Nurse"}
            </option>
            {doctors.map((doctor) => (
              <option key={doctor._id} value={doctor._id}>
                {doctor.name}
              </option>
            ))}
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
            <svg
              className="w-4 h-4 text-black"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>
      </div>

      {/* Main Grid - Responsive Layout */}
      <div className="flex flex-col xl:flex-row gap-4 sm:gap-6 lg:gap-8">
        {/* Session Table */}
        <div className="flex-1 overflow-x-auto">
          {loadingSessions ? (
            <TableSkeleton />
          ) : (
            <div className="w-full">
              {/* Desktop Table Header */}
              <div className="hidden md:grid grid-cols-5 gap-4 items-center text-xs bg-gray-100 font-medium text-gray-600 uppercase tracking-wider mb-4 px-4 py-4 rounded-lg">
                <div>Patient Name</div>
                <div>Doctors</div>
                <div>Type</div>
                <div>Date & Time</div>
                <div>Status</div>
              </div>

              {/* Mobile Card Layout */}
              <div className="md:hidden space-y-3">
              {sessions.map((item, idx) => (
                <div
                  key={idx}
                    className="bg-white border rounded-xl p-4 shadow-sm hover:bg-blue-50 transition border-gray-200 hover:border-blue-300"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <img
                          src={getProfileImageUrl(item.patient.profileImage)}
                          alt={item.patient.name}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                        <div>
                          <span className="text-sm font-medium text-gray-900 block">
                            {item.patient.name}
                          </span>
                          <span className="text-xs text-gray-500">Patient</span>
                        </div>
                      </div>
                      <span
                        className={`text-xs px-2 py-1 rounded-full font-medium ${
                          item.status === "scheduled"
                            ? "bg-blue-100 text-blue-600"
                            : item.status === "in_progress"
                            ? "bg-yellow-100 text-yellow-600"
                            : item.status === "completed"
                            ? "bg-green-100 text-green-600"
                            : item.status === "cancelled"
                            ? "bg-red-100 text-red-600"
                            : item.status === "missed"
                            ? "bg-gray-100 text-gray-600"
                            : "bg-gray-200 text-gray-600"
                        }`}
                      >
                        {item.status === "in_progress" ? "In Progress" : 
                         item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                      </span>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <img
                          src={getProfileImageUrl(item.doctor.profileImage)}
                          alt={item.doctor.name}
                          className="w-6 h-6 rounded-full object-cover"
                        />
                        <span className="text-sm text-gray-700">
                          Dr. {item.doctor.name}
                        </span>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <span className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-full">
                        {formatSessionType(item.sessionType)}
                        </span>
                        <div className="text-right">
                          {item.date && item.startTime ? (
                            <div className="text-sm text-gray-600">
                              <div className="font-medium">
                                {(() => {
                                  let dateObj;
                                  if (item.startTime.toLowerCase().includes('am') || item.startTime.toLowerCase().includes('pm')) {
                                    dateObj = new Date(`${item.date} ${item.startTime}`);
                                  } else {
                                    dateObj = new Date(`${item.date}T${item.startTime}`);
                                  }

                                  const dateString = dateObj.toLocaleDateString("en-GB", {
                                    day: "2-digit",
                                    month: "long",
                                    year: "numeric",
                                  });

                                  let hours = dateObj.getHours();
                                  let minutes = dateObj.getMinutes();
                                  const ampm = hours >= 12 ? "pm" : "am";
                                  hours = hours % 12 || 12;
                                  const timeString = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")} ${ampm}`;

                                  return (
                                    <span>
                                      {dateString} at {timeString}
                                    </span>
                                  );
                                })()}
                              </div>
                            </div>
                          ) : (
                            <span className="text-gray-400">No date set</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table Layout */}
              <div className="hidden md:block">
                {sessions.map((item, idx) => (
                  <div
                    key={idx}
                    className="grid grid-cols-5 gap-4 items-center border rounded-xl px-4 py-4 my-2 bg-white shadow-sm hover:bg-blue-50 transition border-gray-200 hover:border-blue-300"
                >
                  <div className="flex items-center space-x-2">
                    <img
                      src={getProfileImageUrl(item.patient.profileImage)}
                      alt={item.patient.name}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                    <span className="text-sm text-gray-900 font-medium">
                      {item.patient.name}
                    </span>
                  </div>

                  <div className="flex items-center space-x-2">
                    <img
                      src={getProfileImageUrl(item.doctor.profileImage)}
                      alt={item.doctor.name}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                    <span className="text-sm text-gray-700">
                      {item.doctor.name}
                    </span>
                  </div>

                  <div>
                    <span className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-full">
                      {formatSessionType(item.type)}
                    </span>
                  </div>

                  <div className="text-sm text-gray-600">
                    {item.date && item.startTime ? (
                      <div>
                        {(() => {
                          let dateObj;
                          if (item.startTime.toLowerCase().includes('am') || item.startTime.toLowerCase().includes('pm')) {
                            dateObj = new Date(`${item.date} ${item.startTime}`);
                          } else {
                            dateObj = new Date(`${item.date}T${item.startTime}`);
                          }

                          const dateString = dateObj.toLocaleDateString("en-GB", {
                              day: "2-digit",
                              month: "long",
                              year: "numeric",
                          });

                          let hours = dateObj.getHours();
                          let minutes = dateObj.getMinutes();
                          const ampm = hours >= 12 ? "pm" : "am";
                          hours = hours % 12 || 12;
                          const timeString = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")} ${ampm}`;

                          return (
                            <span>
                              {dateString} at {timeString}
                            </span>
                          );
                        })()}
                      </div>
                    ) : (
                      <span className="text-gray-400">No date set</span>
                    )}
                  </div>

                  <div>
                    <span
                      className={`text-xs px-2 py-1 rounded-full font-medium ${
                        item.status === "scheduled"
                          ? "bg-blue-100 text-blue-600"
                          : item.status === "in_progress"
                          ? "bg-yellow-100 text-yellow-600"
                          : item.status === "completed"
                          ? "bg-green-100 text-green-600"
                          : item.status === "cancelled"
                          ? "bg-red-100 text-red-600"
                          : item.status === "missed"
                          ? "bg-gray-100 text-gray-600"
                          : "bg-gray-200 text-gray-600"
                      }`}
                    >
                      {item.status === "in_progress" ? "In Progress" : 
                       item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                    </span>
                  </div>
                </div>
              ))}
            </div>

              {/* No Data Found Message */}
              {sessions.length === 0 && !loadingSessions && (
                <div className="text-center py-8 sm:py-12 bg-white rounded-xl border border-gray-200">
                  <div className="flex flex-col items-center">
                    <svg
                      className="w-12 h-12 sm:w-16 sm:h-16 text-gray-300 mb-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1"
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">No sessions found</h3>
                    <p className="text-sm sm:text-base text-gray-500 mb-4 px-4">
                      {filters.patientName || filters.status || filters.sessionType || filters.nurseId || filters.startDate || filters.endDate
                        ? "No sessions match your current filters. Try adjusting your search criteria."
                        : "There are no sessions available at the moment."}
                    </p>
                    {(filters.patientName || filters.status || filters.sessionType || filters.nurseId || filters.startDate || filters.endDate) && (
                      <button
                        onClick={() => {
                          setFilters({
                            patientName: "",
                            status: "",
                            sessionType: "",
                            nurseId: "",
                            startDate: null,
                            endDate: null,
                          });
                        }}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                      >
                        Clear All Filters
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Pagination */}
            {sessions.length > 0 && pagination.totalPages > 1 && (
          <div className="bg-white border-t border-gray-200 px-3 sm:px-4 py-3 flex items-center justify-between mt-4">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={!pagination.hasPrevPage || pagination.currentPage <= 1}
                className="relative inline-flex items-center px-3 sm:px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed min-w-[80px] justify-center"
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={!pagination.hasNextPage || pagination.currentPage >= pagination.totalPages}
                className="ml-3 relative inline-flex items-center px-3 sm:px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed min-w-[80px] justify-center"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{((pagination.currentPage - 1) * pagination.limit) + 1}</span> to <span className="font-medium">{Math.min(pagination.currentPage * pagination.limit, pagination.totalSessions)}</span> of{' '}
                  <span className="font-medium">{pagination.totalSessions}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    disabled={!pagination.hasPrevPage || pagination.currentPage <= 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Previous</span>
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>

                  {/* Page numbers */}
                  {(() => {
                    const totalPages = pagination.totalPages;
                    const currentPage = pagination.currentPage;
                    const pagesToShow = Math.min(5, totalPages);
                    
                    let startPage, endPage;
                    
                    if (totalPages <= 5) {
                      startPage = 1;
                      endPage = totalPages;
                    } else if (currentPage <= 3) {
                      startPage = 1;
                      endPage = 5;
                    } else if (currentPage >= totalPages - 2) {
                      startPage = totalPages - 4;
                      endPage = totalPages;
                    } else {
                      startPage = currentPage - 2;
                      endPage = currentPage + 2;
                    }
                    
                    const pages = [];
                    for (let i = startPage; i <= endPage; i++) {
                      pages.push(i);
                    }
                    
                    return pages.map(pageNum => {
                      const isActive = pageNum === currentPage;
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            isActive
                              ? 'bg-blue-50 border-blue-500 text-blue-600'
                              : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    });
                  })()}

                  <button
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    disabled={!pagination.hasNextPage || pagination.currentPage >= pagination.totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Next</span>
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}

        </div>

        {/* Sidebar - Responsive */}
        <div className="w-full xl:w-1/4 flex flex-col gap-4 sm:gap-6 lg:gap-8 text-sm sm:text-[15px]">
          {/* Overview */}
         <div className="bg-white p-4 sm:p-6 lg:p-8 rounded-xl shadow-md w-full">
  <div className="flex justify-between items-center mb-4 sm:mb-6">
    <h3 className="text-base sm:text-lg font-semibold text-[#2C1E66]">Overview</h3>
  </div>

  {loadingStats ? (
    <div className="grid grid-cols-2 gap-3 sm:gap-4">
      {[...Array(4)].map((_, idx) => (
        <div key={idx} className="bg-gray-50 rounded-lg p-4 sm:p-6 shadow-md">
          <div className="h-6 bg-gray-200 rounded w-8 animate-pulse mb-2"></div>
          <div className="h-3 bg-gray-200 rounded w-16 animate-pulse"></div>
        </div>
      ))}
    </div>
  ) : (
    <div className="grid grid-cols-2 gap-3 sm:gap-4">
      <div className="bg-green-50 rounded-lg p-4 sm:p-6 shadow-md">
        <p className="text-lg sm:text-xl font-bold">{sessionStats.ongoing || 0}</p>
        <p className="text-xs text-gray-700">In Progress</p>
      </div>
      <div className="bg-red-50 rounded-lg p-4 sm:p-6 shadow-md">
        <p className="text-lg sm:text-xl font-bold">{sessionStats.pending || 0}</p>
        <p className="text-xs text-gray-700">Scheduled</p>
      </div>
      <div className="bg-gray-50 rounded-lg p-4 sm:p-6 shadow-md">
        <p className="text-lg sm:text-xl font-bold">{sessionStats.missed || 0}</p>
        <p className="text-xs text-gray-700">Missed</p>
      </div>
      <div className="bg-blue-50 rounded-lg p-4 sm:p-6 shadow-md">
        <p className="text-lg sm:text-xl font-bold">{sessionStats.completed || 0}</p>
        <p className="text-xs text-gray-700">Completed</p>
      </div>
    </div>
  )}
</div>

          {/* Today Schedule */}
          <div className="bg-white p-4 sm:p-5 rounded-xl shadow-md w-full">
            <div className="flex justify-between mb-3 items-center">
              <h3 className="text-base sm:text-lg font-semibold">Today Schedule</h3>
              <div className="flex gap-2">
                {todaySchedule.length > 3 && !showAllTodaySchedule && (
                  <button
                  className="text-xs text-blue-500 hover:underline"
                    onClick={() => setShowAllTodaySchedule(true)}
                >
                  See All
                </button>
                )}
                {showAllTodaySchedule && todaySchedule.length > 3 && (
                  <button
                    className="text-xs text-blue-500 hover:underline"
                    onClick={() => setShowAllTodaySchedule(false)}
                  >
                    Show Less
                  </button>
                )}
              </div>
            </div>
            {loadingTodaySchedule ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, idx) => (
                  <div key={idx} className="flex items-center gap-3 text-sm">
                    <div className="w-9 h-9 bg-gray-200 rounded-full animate-pulse"></div>
                    <div className="flex-1">
                      <div className="h-4 bg-gray-200 rounded w-24 animate-pulse mb-1"></div>
                      <div className="h-3 bg-gray-200 rounded w-32 animate-pulse"></div>
                    </div>
                    <div className="h-3 bg-gray-200 rounded w-12 animate-pulse"></div>
                  </div>
                ))}
              </div>
            ) : (
              <ul className="space-y-4">
                {(showAllTodaySchedule ? todaySchedule : todaySchedule.slice(0, 3)).length > 0 ? (
                  (showAllTodaySchedule ? todaySchedule : todaySchedule.slice(0, 3)).map((session, idx) => (
                    <li key={idx} className="flex items-center gap-3 text-sm">
                      <div className="w-9 h-9 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
                        <img
                          src={getProfileImageUrl(session.patient?.profileImage)}
                          alt={session.patient?.name || "Patient"}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex justify-between items-center w-full">
                        <div className="text-gray-700 leading-tight flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">{session.patient?.name || "Unknown Patient"}</p>
                          <p className="text-xs text-gray-400 truncate">{session.doctor?.name || "Unknown Provider"}</p>
                        </div>
                        <p className="text-xs font-semibold text-gray-500 whitespace-nowrap ml-2">
                          {formatTime(session.scheduledAt)}
                        </p>
                      </div>
                    </li>
                  ))
                ) : (
                  <li className="text-sm text-gray-500 text-center py-4">
                    No sessions scheduled for today
                  </li>
                )}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Session;